// eslint-disable-next-line no-unused-vars
import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { array, bool, func, string, object, number } from 'prop-types';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { Filter, ArrowDownWideNarrow, X } from 'lucide-react';
import {
  DatePicker,
  DatePickerInput,
  TextInput,
  Button,
  SkeletonPlaceholder,
  RadioButtonGroup,
  RadioButton,
} from '@carbon/react';

import { NWCheckbox, NWComboBox } from 'Components/shared';

const StyledFilterOrder = styled.div`
  .mobileToolbar {
    display: none;
    gap: 1em;
    margin-bottom: 18px;

    &.fixed {
      display: none;
      justify-content: center;
      align-items: center;
      padding: 15px;
      margin: 0;
      width: 100vw;
      height: 80px;
      position: fixed;
      bottom: 0;
      left: 0;
      transition: transform 0.2s ease;
      overflow: visible;
      opacity: ${ props => props.isVisible ? 1 : 0 };
      visibility: ${ props => props.isVisible ? 'visible' : 'hidden' };
      background-color: ${ props => props.theme.colors.white };
      box-shadow: 0 3px 6px #00000029;
      z-index: 8999;
    }

    button {
      display: flex;
      flex: 1;
      justify-content: center;
      align-items: center;
      flex-direction: column;
      padding: 8px;
      background: #E7ECF2;
      border: 1px solid #B2C2D5;
      border-radius: 10px;
      cursor: pointer;

      .title {
        display: flex;
        align-items: center;
        font-size: 16px;
        line-height: 24px;
        font-weight: 600;

        svg {
          margin-right: 8px;
        }
      }

      .info {
        color: #707070;
        font-size: 12px;
        line-height: 16px;
      }
    }
  }

  .backDrop {
    position: fixed;
    inset: 0;
    z-index: 10;
    background: #000000b3;
    display: ${ props => props.mobileSortOpen ? 'flex' : 'none' };
    align-items: flex-end;

    .content {
      flex: 1;
      padding: 15px;
      background: #fff;

      .header {
        display: flex;
        justify-content: space-between;
        margin-bottom: 10px;

        h3 {
          font-size: 20px;
          line-height: 27px;
        }

        button {
          background: transparent;
          border: none;
          cursor: pointer;
        }
      }

      .cds--radio-button-group {
        display: flex;
        flex-direction: column;
        align-items: flex-start;

        .cds--radio-button-wrapper {
          margin: 12px 0;
        }
      }
    }
  }

  .filterSection {
    display: flex;
    flex-direction: column;
    padding: 25px 0;
    gap: 20px;

    .mobileHeader {
      display: none;

      .mobileTitleSection {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 25px 15px 15px;

        h2 {
          font-size: 20px;
        }

        button {
          cursor: pointer;
          color: #D52B1E;
          background: transparent;
          border: none;
        }
      }

      .activeFilters {
        display: flex;
        background: #F3F2F2;
        padding: 15px;
        flex-direction: column;

        > span {
          font-weight: 600;
        }

        .activeFilterList {
          padding: 10px 0;
          display: flex;
          text-wrap: nowrap;
          overflow-x: scroll;

          .filter { 
            margin-right: 10px;
          }
        }

        button {
          cursor: pointer;
          background: transparent;
          border: none;

          svg {
            fill: #D52B1E;
          }
        }
      }
    }

    .filters {
      display: flex;

      > div {
        padding: 0 5px;
        flex: 0 1 auto;
        min-width: 12.5%;

        .cds--combo-box {
          margin-bottom: 0;
        }

        &:first-of-type {
          padding-left: 0;
        }

        &:last-of-type {
          padding-right: 0;
        }

        &:nth-of-type(5) {
          flex: 1;
          min-width: 37.5%;
        }
      }
    }

    .actionsSection {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 10px;

      .dropShipping {
        display: flex;
        gap: 10px;
        white-space: nowrap;

        > div:first-of-type {
          display: flex;
          align-items: center;
          margin-bottom: 0;
        }

        .dateRange {
          display: flex;

          > div {
            padding: 0 5px;
            flex: 0 1 auto;
            min-width: 50%;
          }

          .cds--date-picker.cds--date-picker--single .cds--date-picker__input {
            width: 100%;
          }
        }
      }

      .actions {
        display: flex;
        gap: 10px;

        button.cds--btn {
          height: 40px;
          max-width: none;
          min-width: 170px;
          -webkit-box-pack: center;
          justify-content: center;
          font-size: 1rem;
          text-align: center;

          &.deleteFilters {
            border: 1px solid #D52B1E;
            color: #D52B1E;

            &:hover {
              background: transparent;
            }
          }
        }
      }
    }

    .cds--label {
      display: none;
    }

    .cds--select-input, .cds--date-picker__input, .cds--text-input {
      background: #fff;
      border: 1px solid #e2e2e2;
      border-radius: 5px;
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.laptop }) {
    .filterSection {
      display: ${ props => props.mobileFiltersOpen ? 'flex' : 'none' };
      position: fixed;
      inset: 0;
      background: #fff;
      z-index: 9001;
      margin-top: 0;
      overflow-y: scroll;

      .cds--label {
        display: inline-block;
        font-size: 15px;
        font-weight: 600;
        color: #3a3a3a;
      }

      .mobileHeader {
        display: block;
      }

      .filters {
        gap: 20px;
        flex-direction: column;
        padding: 15px;

        .cds--date-picker {
          width: 100%;

          .cds--date-picker-container {
            width: 100%;
          }
        }
      }

      .actionsSection {
        gap: 20px;
        flex-direction: column;
        padding: 15px;

        .dropShipping {
          flex-direction: column;
          width: 100%;
        }

        .actions {
          width: 100%;

          button.cds--btn {
            min-width: 100%;

            &.deleteFilters {
              display: none;
            }
          }
        }
      }
    }

    .mobileToolbar {
      display: flex;

      &.fixed {
        display: flex;
      }
    }
  }
`;

const StyledSkeleton = styled(SkeletonPlaceholder)`
  width: 100%;
  margin: 20px 0;
  height: 35px;
`;

const FilterOrders = ({
  filterKey,
  register,
  control,
  getValues,
  setValue,
  onSubmit,
  onClearFilters,
  onClearFilter,
  onChangeDate,
  states,
  types,
  deliveryStockOptions,
  users,
  usersLoading,
  startDate,
  endDate,
  setMobileFiltersOpen,
  mobileFiltersOpen,
  setMobileSortOpen,
  handleMobileOrderChange,
  activeFilters,
  currentSort,
  sortOptions,
  mobileSortOpen,
  isExternalReseller,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const filterKeys = useMemo(() => [
    { key: 'internal_status_id', label: t('orders.state') },
    { key: 'user', label: t('orders.user') },
    { key: 'order_type', label: t('orders.orderType') },
    { key: 'delivery_stock', label: t('orders.deliveryStock') },
    { key: 'start_date', label: t('orders.dateStart') },
    { key: 'end_date', label: t('orders.dateEnd') },
    { key: 'product', label: t('orders.searchProductBy') },
    { key: 'drop_shipment', label: t('orders.showDropshippingOrders') },
    { key: 'reference', label: t('orders.internalRef') },
  ], []);

  const renderFilter = ({ key, value }) => {
    const filterName = filterKeys.find(item => item.key === key)?.label;
    let filterValue = value;

    if (key === 'internal_status_id') {
      filterValue = states.find(item => item.value === value)?.label;
    }

    if (key === 'user') {
      filterValue = users.find(item => +item.value === +value)?.label;
    }

    if (key === 'order_type') {
      filterValue = types.find(item => item.value === value)?.label;
    }

    if (key === 'delivery_stock') {
      filterValue = deliveryStockOptions.find(item => item.value === value)?.label;
    }

    if (key === 'drop_shipment') {
      filterValue = value ? t('common.yes') : t('common.no');
    }

    return (
      <div>
        <span>{ `${ filterName }: ${ filterValue }` }</span>
        <button type="button" onClick={ () => onClearFilter(key) }><X /></button>
      </div>
    );
  };

  const sharedProps = fieldName => ({
    id: fieldName,
    name: fieldName,
    ...register(fieldName),
  });

  const sharedComboBoxProps = fieldName => ({
    id: fieldName,
    name: fieldName,
    control,
  });

  return usersLoading ? <StyledSkeleton /> : (
    <StyledFilterOrder theme={ theme } mobileSortOpen={ mobileSortOpen } mobileFiltersOpen={ mobileFiltersOpen }>
      <div className="mobileToolbar">
        <button type="button" onClick={ () => setMobileFiltersOpen(true) }>
          <span className="title">
            <Filter />
            { t('filters.filter') }
          </span>
          <span className="info">{ `${ Object.keys(activeFilters).length } ${t('filters.selectedFilters')}`} </span>
        </button>
        <button type="button" onClick={ () => setMobileSortOpen(true) }>
          <span className="title">
            <ArrowDownWideNarrow />
            { t('products.sort') }
          </span>
          <span className="info">{ currentSort?.label }</span>
        </button>
      </div>
      <div className="mobileSort">
        <div className="backDrop">
          <div className="content">
            <div className="header">
              <h3>{ t('products.sortBy') }</h3>
              <button type="button" onClick={ () => setMobileSortOpen(false) }>
                <X />
              </button>
            </div>
            <RadioButtonGroup
              name="sortOrders"
              valueSelected={ currentSort?.id }
              onChange={ handleMobileOrderChange }
            >
              { sortOptions.map(opt => (
                <RadioButton key={ opt.id } labelText={ opt.label } value={ opt.id } id={ opt.id } />
              )) }
            </RadioButtonGroup>
          </div>
        </div>
      </div>
      <div className="filterSection">
        <div className="mobileHeader">
          <div className="mobileTitleSection">
            <h2>{ t('orders.filters') }</h2>
            <button
              type="button"
              onClick={ () => {
                onClearFilters();
                setMobileFiltersOpen(false);
              } }
            >
              { t('products.clearAllFilters')}
            </button>
          </div>
          { Object.keys(activeFilters).length > 0 && (
            <div className="activeFilters">
              <span>{ `${ Object.keys(activeFilters).length } ${ t('orders.activeFilters') }` }</span>
              <div className="activeFilterList">
                { Object.entries(activeFilters).map(([key, value]) => <div key={ key } className="filter">{ renderFilter({ key, value }) }</div>) }
              </div>
            </div>
          )}
        </div>
        <div className="filters">
          <NWComboBox
            key={ `state-${ filterKey }` }
            titleText={ t('orders.state') }
            placeholder={ t('orders.state') }
            light
            { ...sharedComboBoxProps('internal_status_id') }
            items={ states }
            onChange={ value => setValue('internal_status_id', value?.selectedItem?.value) }
            initialSelectedItem={ states.find(item => item.value === getValues('internal_status_id')) || '' }
          />
          <NWComboBox
            key={ `user-${ filterKey }` }
            titleText={ t('orders.user') }
            placeholder={ t('orders.user') }
            light
            { ...sharedComboBoxProps('user') }
            items={ users }
            onChange={ value => setValue('user', value?.selectedItem?.value) }
            initialSelectedItem={ users.find(item => item.value === getValues('user')) || '' }
          />
          <NWComboBox
            key={ `type-${ filterKey }` }
            titleText={ t('orders.orderType') }
            placeholder={ t('orders.orderType') }
            light
            { ...sharedComboBoxProps('order_type') }
            items={ types }
            onChange={ value => setValue('order_type', value?.selectedItem?.value) }
            initialSelectedItem={ types.find(item => item.value === getValues('order_type')) || '' }
          />
          <NWComboBox
            key={ `delivery-${ filterKey }` }
            titleText={ t('orders.deliveryStock') }
            placeholder={ t('orders.deliveryStock') }
            light
            { ...sharedComboBoxProps('delivery_stock') }
            items={ deliveryStockOptions }
            onChange={ value => setValue('delivery_stock', value?.selectedItem?.value) }
            initialSelectedItem={ deliveryStockOptions.find(item => item.value === getValues('delivery_stock')) || '' }
          />
          <TextInput
            id="search"
            labelText={ t('orders.searchProductBy') }
            placeholder={ t('orders.searchProductBy') }
            size="md"
            { ...sharedProps('product') }
          />
        </div>
        <div className="actionsSection">
          <div className="dropShipping">
            { !isExternalReseller && (
              <>
                <NWCheckbox
                  labelText={ t('orders.showDropshippingOrders') }
                  { ...sharedProps('drop_shipment') }
                  onChange={ value => setValue('drop_shipment', value) }
                />
                <TextInput
                  id="search"
                  labelText={ t('orders.internalRef') }
                  placeholder={ t('orders.internalRef') }
                  size="md"
                  { ...sharedProps('reference') }
                />
              </>
            ) }
            <div className="dateRange">
              <DatePicker
                dateFormat="Y-m-d"
                datePickerType="single"
                value={ startDate }
                maxDate={ endDate }
                onChange={ date => onChangeDate('start_date', date) }
              >
                <DatePickerInput
                  placeholder={ t('orders.dateStart') }
                  labelText={ t('orders.dateStart') }
                  id="date-start"
                  size="md"
                />
              </DatePicker>
              <DatePicker
                dateFormat="Y-m-d"
                datePickerType="single"
                value={ endDate }
                minDate={ startDate }
                onChange={ date => onChangeDate('end_date', date) }
              >
                <DatePickerInput
                  placeholder={ t('orders.dateEnd') }
                  labelText={ t('orders.dateEnd') }
                  id="date-end"
                  size="md"
                />
              </DatePicker>
            </div>
          </div>
          <div className="actions">
            <Button
              className="filterOrders"
              kind="danger"
              onClick={ onSubmit }
            >
              { t('orders.filter') }
            </Button>
            { !!Object.keys(activeFilters).length && (
              <Button
                className="deleteFilters"
                kind="danger--ghost"
                onClick={ onClearFilters }
              >
                { t('order.deleteFilters') }
              </Button>
            )}
          </div>
        </div>
      </div>
    </StyledFilterOrder>
  );
};

FilterOrders.propTypes = {
  filterKey: number,
  register: func,
  control: object,
  getValues: func,
  setValue: func,
  onSubmit: func,
  onClearFilters: func,
  onClearFilter: func,
  onChangeDate: func,
  states: array,
  types: array,
  deliveryStockOptions: array,
  users: array,
  usersLoading: bool,
  startDate: string,
  endDate: string,
  setMobileFiltersOpen: func,
  setMobileSortOpen: func,
  handleMobileOrderChange: func,
  activeFilters: object,
  currentSort: object,
  mobileFiltersOpen: bool,
  sortOptions: array,
  mobileSortOpen: bool,
  isExternalReseller: bool,
};

FilterOrders.displayName = 'FilterOrdersComponent';

export default FilterOrders;
